import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'
import { baseModelShape } from '../base-model'
import { expenseGroupShape } from '../expenses/models'
import { paymentAccountShape } from '../payment-accounts/models'
import { reportShape } from '../expense-reports/models'
import { userShape } from '../user'

export const statementStatusEnum = {
  UPLOADED: 'uploaded',
  PROCESSING: 'processing',
  READY: 'ready',
  RECONCILING: 'reconciling',
  IN_REVIEW: 'in_review',
  CONFIRMED: 'confirmed',
  FAILED: 'failed',
} as const

export type StatementStatusValues = (typeof statementStatusEnum)[keyof typeof statementStatusEnum]

// i18n keys (not hardcoded copy) so the list page and review header share one
// label source. `uploaded`/`processing` read as "Processing" (system parsing),
// `ready` reads as "Action needed" (waiting on the Account or Columns step),
// `reconciling` as "Matching" (system matching rows), and the remaining states
// surface the user-facing outcome.
export const statementStatusLabelKeys: Record<StatementStatusValues, string> = {
  [statementStatusEnum.UPLOADED]: 'enums.statementStatus_processing',
  [statementStatusEnum.PROCESSING]: 'enums.statementStatus_processing',
  [statementStatusEnum.READY]: 'enums.statementStatus_actionNeeded',
  [statementStatusEnum.RECONCILING]: 'enums.statementStatus_matching',
  [statementStatusEnum.IN_REVIEW]: 'enums.statementStatus_inReview',
  [statementStatusEnum.CONFIRMED]: 'enums.statementStatus_completed',
  [statementStatusEnum.FAILED]: 'enums.statementStatus_failed',
}

export const statementStatusBadgeClasses: Record<StatementStatusValues, string> = {
  [statementStatusEnum.UPLOADED]: 'bg-gray-100 text-gray-700',
  [statementStatusEnum.PROCESSING]: 'bg-gray-100 text-gray-700',
  [statementStatusEnum.READY]: 'bg-accent-50 text-accent-700',
  [statementStatusEnum.RECONCILING]: 'bg-gray-100 text-gray-700',
  [statementStatusEnum.IN_REVIEW]: 'bg-accent-50 text-accent-700',
  [statementStatusEnum.CONFIRMED]: 'bg-green-100 text-green-700',
  [statementStatusEnum.FAILED]: 'bg-error-100 text-error-700',
}

export const classificationEnum = {
  MATCHED: 'matched',
  UNMATCHED: 'unmatched',
} as const

export type ClassificationValues = (typeof classificationEnum)[keyof typeof classificationEnum]

export const decisionEnum = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const

export type DecisionValues = (typeof decisionEnum)[keyof typeof decisionEnum]

export const statementShape = {
  ...baseModelShape,
  statementFile: z.string(),
  normalizedCsv: z.string().nullable().optional(),
  fileFormat: z.string(),
  uploadedBy: z.string(),
  uploadedByRef: z.object(userShape).nullable().optional(),
  // Optional at upload; settled in the Account wizard step.
  paymentAccount: z.uuid().nullable(),
  paymentAccountRef: z.object(paymentAccountShape).nullable().optional(),
  paymentAccountConfirmed: z.boolean().optional(),
  suggestedPaymentAccount: z.uuid().nullable().optional(),
  suggestedPaymentAccountReason: z.string().optional(),
  // Org that gates and pays for every AI step; null means AI steps are skipped.
  billingOrganization: z.uuid().nullable().optional(),
  billingOrganizationName: z.string().nullable().optional(),
  status: z.enum(statementStatusEnum),
  rowCount: z.number(),
  errorMessage: z.string().nullable().optional(),
  suggestedReport: z.string().nullable().optional(),
  suggestedReportRef: z.object(reportShape).nullable().optional(),
  reportDistribution: z.any().nullable().optional(),
  // Header map (raw header → normalized column) present whenever the alias table
  // could not fully satisfy the required columns and a map was needed — whether
  // AI-prefilled or mapped by hand — plus whether the user has confirmed it.
  // Drives the Columns wizard step.
  columnMap: z.record(z.string(), z.any()).nullable().optional(),
  columnMapConfirmed: z.boolean().nullable().optional(),
  // Present only on the upload/reconcile responses: the tracked AsyncJob's id,
  // so the client can optimistically show an in-progress spinner immediately.
  asyncJobId: z.string().uuid().nullable().optional(),
}

export const rawDataShape = {
  date: z.string().optional(),
  amount: z.union([z.string(), z.number()]).optional(),
  description: z.string().optional(),
  balance: z.union([z.string(), z.number()]).optional().nullable(),
}

export const statementRowShape = {
  ...baseModelShape,
  statement: z.string(),
  rowIndex: z.number(),
  rawData: z.object(rawDataShape),
  classification: z.enum(classificationEnum).nullable().optional(),
  matchedExpense: z.string().nullable().optional(),
  matchedExpenseRef: z.object(expenseGroupShape).nullable().optional(),
  decision: z.enum(decisionEnum),
  confidenceScore: z.number().nullable().optional(),
  autofilledFields: z.record(z.string(), z.any()).nullable().optional(),
  llmReasoning: z.string().nullable().optional(),
}

export const statementFilterShape = {
  paymentAccount: z.array(z.string()),
  status: z.array(z.string()),
  organization: z.array(z.string()),
}

export const confirmRowActionShape = {
  id: z.string(),
  action: z.enum(['approve', 'reject']),
  autofilledFields: z.record(z.string(), z.any()).optional(),
}

export const confirmResponseShape = {
  reconciled: z.array(z.string()),
  created: z.array(z.string()),
  skipped: z.number(),
}

export type Statement = GetInferredFromRaw<typeof statementShape>
export type StatementRow = GetInferredFromRaw<typeof statementRowShape>
export type StatementFilter = GetInferredFromRaw<typeof statementFilterShape>
export type ConfirmRowAction = GetInferredFromRaw<typeof confirmRowActionShape>
export type ConfirmResponse = GetInferredFromRaw<typeof confirmResponseShape>
